<template>
	<component class="tippy-wrapper" :is="tag"><slot /></component>
</template>

<script>
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/scale.css'
import { sanitizeHTML } from '@/helpers/utils'

const defaultOptions = {
	interactive: true,
}

export default {
	name: 'Tippy',
	props: {
		content: null,
		arrow: { type: Boolean, default: null },
		safe: { type: Boolean, default: null },
		theme: {
			type: String,
			default: 'small',
			validator: (value) =>
				['small', 'large', 'bluewhite', 'scale'].includes(value),
		},
		options: {
			type: Object,
			default: () => ({}),
		},
		tag: {
			type: String,
			default: 'span',
		},
	},
	mounted() {
		this.initTip()
	},
	beforeUnmount() {
		this.destroyInstance()
	},
	watch: {
		content() {
			this.setContent()
		},
		allOptions() {
			this.initTip()
		},
	},
	computed: {
		allOptions() {
			const { textAlign, ...rest } = this.options || {}
			return {
				...defaultOptions,
				arrow: this.arrow,
				theme: this.theme || '',
				content: this.sanitized,
				zIndex: 10001,
				appendTo: () => document.body,
				onShow: () => {
					if (this.options.maxWidth) {
						this.instance.popper.style.maxWidth = this.options.maxWidth
					}
					if (textAlign) {
						this.instance.popper.style.textAlign = textAlign
					}
				},
				...rest,
			}
		},
		sanitized() {
			// setting 'safe=true' prop tells tippy to bypass sanitization
			// and assumes that the content is already sanitized by parent component
			if (this.safe) return this.content
			return sanitizeHTML(this.content?.outerHTML ?? this.content ?? '')
		},
	},
	methods: {
		initTip() {
			if (!this.content) return
			this.destroyInstance()
			this.instance = tippy(this.$el, this.allOptions)
			this.setContent()
		},
		setContent() {
			if (!this.$el?._tippy) return
			if (!this.content) {
				return this.destroyInstance()
			}
			if (this.instance) {
				return this.$el._tippy.setContent(this.sanitized)
			}
			this.initTip()
		},
		destroyInstance() {
			if (!this.instance) return
			if (this.instance.destroy) {
				this.instance.destroy()
			} else if (this.instance.destroyAll) {
				this.instance.destroyAll()
			}
			this.instance = null
		},
	},
}
</script>

<style lang="scss">
@import 'src/sass/variables';

.tippy-box {
	color: white;
	background: var(--neutral-600);

	&[data-theme~='small'] {
		.tippy-content {
			font-size: 12px;
			line-height: 1;
		}
		.tippy-arrow::before {
			border-top-color: var(--neutral-600);
		}
	}

	&[data-theme~='large'] {
		background-color: var(--neutral-600);
		.tippy-content {
			padding: 5px 15px;
			font-size: 14px;
			a {
				color: var(--teal-300) !important;
				text-decoration: none !important;
			}
		}
		.tippy-arrow::before {
			border-top-color: var(--neutral-600);
		}
	}

	&[data-theme~='bluewhite'] {
		background-color: $pronto-blue;
		.tippy-content {
			color: #fff;
			padding: 5px 15px;
			font-size: 14px;
			font-family: Avenir-Book;
		}
		.tippy-arrow::before {
			border-top-color: $pronto-blue;
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.tippy-box {
		color: white;
		background: var(--neutral-700);
		.tippy-arrow::before {
			border-top-color: var(--neutral-700);
		}
	}
}
</style>
